import { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Section, StatusPill } from "@/components/Shell";
import { LogoFrame, ClientPlaceholderMark } from "@/components/Logos";
import { ADMIN } from "@/constants/testIds";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle2, XCircle, Eye, Save, Loader2, ShieldCheck, FileText,
} from "lucide-react";

const STATUS_OPTIONS = ["submitted", "under_review", "approved", "rejected", "on_hold"];

export default function AdminCandidateDetailPage() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("submitted");
  const [notes, setNotes] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);
  const [viewDoc, setViewDoc] = useState(null); // { fileName, mimeType, url }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/candidates/${id}`);
      setC(data);
      setStatus(data.status);
      setNotes(data.review_notes || "");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const saveStatus = async (override) => {
    const next = override || status;
    setSavingStatus(true);
    try {
      await api.patch(`/admin/candidates/${id}/status`, {
        status: next,
        review_notes: notes,
      });
      toast.success(`Application ${next.replace("_", " ")}`);
      setStatus(next);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingStatus(false);
    }
  };

  const verifyDoc = async (docId, next, reviewer_notes = null) => {
    try {
      await api.patch(`/admin/candidates/${id}/documents/${docId}/verify`, {
        status: next,
        reviewer_notes,
      });
      toast.success(`Document ${next}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const openDoc = async (docId, meta) => {
    try {
      const { data } = await api.get(`/admin/candidates/${id}/documents/${docId}`);
      const url = `data:${data.mime_type};base64,${data.data_base64}`;
      setViewDoc({ url, fileName: data.file_name, mimeType: data.mime_type });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const expiring = useMemo(() => {
    if (!c) return [];
    const today = new Date();
    const in60 = new Date(today.getTime() + 60 * 86400000);
    return (c.documents || []).filter((d) => {
      if (!d.expiry_date) return false;
      const dt = new Date(d.expiry_date);
      return dt >= today && dt <= in60;
    });
  }, [c]);

  const addressLine = useMemo(
    () => (c ? [c.address_line1, c.address_line2, c.postal_code].filter(Boolean).join(", ") : ""),
    [c]
  );

  if (loading || !c)
    return (
      <div className="font-mono text-sm text-black/60">Loading candidate…</div>
    );

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between gap-3">
        <Link to="/admin/candidates" className="btn-brut btn-ghost text-xs">
          <ArrowLeft size={14} /> Back to list
        </Link>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <span
            data-testid={ADMIN.detailClient}
            className="pill pill-ink"
          >
            {c.client_name || "—"}
          </span>
          <span
            data-testid={ADMIN.detailProject}
            className="pill pill-flame"
          >
            {c.project_label || c.project || "—"}
          </span>
          <span
            data-testid={ADMIN.detailRole}
            className="pill pill-teal"
          >
            {c.role_label || c.role || "—"}
          </span>
          <span
            data-testid={ADMIN.detailRef}
            className="pill pill-muted font-mono"
          >
            {c.reference}
          </span>
          <span data-testid={ADMIN.detailStatus}>
            <StatusPill status={c.status} />
          </span>
        </div>
      </div>

      <Section kicker="Candidate" title={c.full_name}>
        {c.client_name && (
          <div className="flex items-center gap-3 -mt-3 mb-6">
            <LogoFrame
              src={c.client_logo_data_url}
              alt={`${c.client_name} logo`}
              size={40}
              fallback={<ClientPlaceholderMark text={c.client_name} size={40} />}
            />
            <div className="font-mono text-xs uppercase tracking-[0.18em] text-black/70">
              Recruited for <b className="text-black">{c.client_name}</b>
            </div>
          </div>
        )}
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="brut-card p-5 lg:col-span-2">
            <div className="grid md:grid-cols-2 gap-x-6 gap-y-2">
              <KV label="Email" value={c.email} />
              <KV label="Phone" value={c.phone} />
              <KV label="Date of birth" value={c.date_of_birth} />
              <KV label="Gender" value={c.gender} />
              <KV label="Nationality" value={c.nationality} />
              <KV label="Country" value={c.country} />
              <KV label="City" value={c.city} />
              <KV label="Address" value={addressLine} />
              <KV label="Emergency contact" value={`${c.emergency_contact_name} (${c.emergency_contact_relation})`} />
              <KV label="Emergency phone" value={c.emergency_contact_phone} />
            </div>
            <div className="dashed-line my-5" />
            <div className="grid md:grid-cols-2 gap-x-6 gap-y-2">
              <KV label="Years experience" value={c.years_experience} />
              <KV label="FTTH years" value={c.ftth_experience_years} />
              <KV label="Previous employer" value={c.previous_employer} />
              <KV label="Previous role" value={c.previous_role} />
              <KV label="Driver's license" value={c.driver_license_number} />
              <KV label="Own tools" value={c.has_own_tools ? "Yes" : "No"} />
            </div>
            {c.skills?.length ? (
              <div className="mt-4">
                <Label>Skills</Label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {c.skills.map((s) => (
                    <span key={s} className="pill pill-muted text-[10px]">{s}</span>
                  ))}
                </div>
              </div>
            ) : null}
            {c.certifications?.length ? (
              <div className="mt-4">
                <Label>Certifications</Label>
                <ul className="text-sm list-disc pl-5">
                  {c.certifications.map((cc, i) => (
                    <li key={`${cc.name}-${cc.year || ""}-${cc.issuer || ""}-${i}`}>
                      {cc.name} — {cc.issuer || "—"} ({cc.year || "—"})
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <div className="dashed-line my-5" />
            <div className="grid md:grid-cols-2 gap-x-6 gap-y-2">
              <KV label="Bank" value={c.bank_name} />
              <KV label="Holder" value={c.bank_account_holder} />
              <KV label="Account" value={c.bank_account_number} />
              <KV label="Routing" value={c.bank_routing_number} />
              <KV label="IBAN" value={c.bank_iban} />
              <KV label="Swift" value={c.bank_swift} />
              <KV label="Tax ID" value={c.tax_id} />
            </div>
          </div>

          {/* Right column: decision + immigration */}
          <div className="space-y-6">
            <div className="brut-card p-5">
              <Label>
                <ShieldCheck size={12} className="inline mr-1 -mt-0.5" />
                Immigration
              </Label>
              <div className="mt-2 space-y-1">
                <KV label="Status" value={c.immigration_status?.replace("_", " ")} />
                <KV label="Work auth. expiry" value={c.work_authorization_expiry} />
                <KV label="Notes" value={c.immigration_notes} />
              </div>
              {expiring.length > 0 && (
                <div className="mt-3 pill pill-flame">
                  {expiring.length} doc(s) expire in 60d
                </div>
              )}
            </div>

            <div className="brut-card p-5">
              <Label>Decision</Label>
              <div className="mt-3 space-y-3">
                <select
                  className="select-brut"
                  data-testid={ADMIN.statusSelect}
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s.replace("_", " ")}</option>
                  ))}
                </select>
                <textarea
                  className="textarea-brut"
                  rows={4}
                  placeholder="Reviewer notes (visible to admin/PM only)"
                  data-testid={ADMIN.reviewNotes}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
                <button
                  className="btn-brut w-full"
                  data-testid={ADMIN.saveStatus}
                  onClick={() => saveStatus()}
                  disabled={savingStatus}
                >
                  {savingStatus ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
                  Save decision
                </button>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    className="btn-brut btn-flame"
                    data-testid={ADMIN.approveBtn}
                    onClick={() => saveStatus("approved")}
                    disabled={savingStatus}
                  >
                    <CheckCircle2 size={14} /> Approve
                  </button>
                  <button
                    className="btn-brut"
                    data-testid={ADMIN.rejectBtn}
                    onClick={() => saveStatus("rejected")}
                    disabled={savingStatus}
                    style={{ boxShadow: "4px 4px 0 0 rgb(199 44 44)" }}
                  >
                    <XCircle size={14} /> Reject
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <VehicleSection
        candidate={c}
        candidateId={id}
        openDoc={openDoc}
        verifyDoc={verifyDoc}
      />

      <Section kicker="Proofs" title={`Documents (${c.documents?.length || 0})`}>
        {(!c.documents || c.documents.length === 0) ? (
          <div className="brut-card p-6 text-black/60">No documents submitted.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {c.documents.map((d) => (
              <div
                key={d.id}
                data-testid={ADMIN.docCard(d.id)}
                className="brut-card p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium truncate">
                      <FileText size={14} className="inline -mt-1 mr-1" />
                      {d.label}
                    </div>
                    <div className="font-mono text-xs text-black/60 mt-1">
                      {d.kind?.replace("_", " ")} · {d.file_name}
                    </div>
                    {d.expiry_date && (
                      <div className="font-mono text-xs mt-1">
                        expires <b>{d.expiry_date}</b>
                      </div>
                    )}
                  </div>
                  <span data-testid={ADMIN.docStatus(d.id)}>
                    <StatusPill status={d.verification_status} />
                  </span>
                </div>
                {d.reviewer_notes && (
                  <div className="mt-2 text-xs text-black/70 italic">
                    “{d.reviewer_notes}”
                  </div>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  <button
                    className="btn-brut btn-ghost text-xs"
                    data-testid={ADMIN.docView(d.id)}
                    onClick={() => openDoc(d.id, d)}
                  >
                    <Eye size={12} /> View
                  </button>
                  <button
                    className="btn-brut text-xs"
                    data-testid={ADMIN.docVerify(d.id)}
                    onClick={() => verifyDoc(d.id, "verified")}
                    style={{ boxShadow: "3px 3px 0 0 rgb(34 139 74)" }}
                  >
                    <CheckCircle2 size={12} /> Verify
                  </button>
                  <button
                    className="btn-brut text-xs"
                    data-testid={ADMIN.docReject(d.id)}
                    onClick={() => {
                      const reason = window.prompt("Why is it rejected? (optional)");
                      verifyDoc(d.id, "rejected", reason || null);
                    }}
                    style={{ boxShadow: "3px 3px 0 0 rgb(199 44 44)" }}
                  >
                    <XCircle size={12} /> Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {viewDoc && (
        <div
          className="fixed inset-0 bg-black/70 z-50 grid place-items-center p-4"
          onClick={() => setViewDoc(null)}
        >
          <div
            className="brut-card bg-[rgb(var(--paper))] max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-3 flex items-center justify-between border-b-2 border-black">
              <div className="font-mono text-xs uppercase tracking-widest">
                {viewDoc.fileName}
              </div>
              <button className="btn-brut btn-ghost text-xs" onClick={() => setViewDoc(null)}>
                Close
              </button>
            </div>
            <div className="flex-1 overflow-auto bg-[rgb(var(--paper-2))]">
              {viewDoc.mimeType.startsWith("image/") ? (
                <img src={viewDoc.url} alt="" className="max-w-full mx-auto" />
              ) : viewDoc.mimeType === "application/pdf" ? (
                <iframe title="doc" src={viewDoc.url} className="w-full h-[80vh]" />
              ) : (
                <a href={viewDoc.url} download={viewDoc.fileName} className="btn-brut m-4 inline-flex">
                  Download {viewDoc.fileName}
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-black/55">
        {label}
      </div>
      <div className="text-sm">{value || "—"}</div>
    </div>
  );
}
function Label({ children }) {
  return (
    <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-black/70">
      {children}
    </div>
  );
}

function VehiclePanel({ title, doc, onView, onVerify, onReject, testKind, candidateId }) {
  return (
    <div className="brut-card p-4" data-testid={`admin-vehicle-${testKind}`}>
      <div className="flex items-center justify-between mb-3">
        <Label>{title}</Label>
        <StatusPill status={doc?.verification_status || "not_ready"} />
      </div>
      {doc ? (
        <>
          <button
            onClick={() => onView(doc.id)}
            className="block w-full border-2 border-black bg-[rgb(var(--paper-2))] hover:bg-[rgb(var(--paper-3))] transition-colors mb-3"
            title="Click to view full size"
            data-testid={`admin-vehicle-${testKind}-view`}
          >
            {doc.mime_type?.startsWith("image/") ? (
              <VehicleThumb candidateId={candidateId} docId={doc.id} fileName={doc.file_name} />
            ) : (
              <div className="p-6 text-center font-mono text-xs">
                📄 {doc.file_name}<br />
                <span className="text-black/60">click to open</span>
              </div>
            )}
          </button>
          <div className="font-mono text-[10px] text-black/60 mb-3">{doc.file_name}</div>
          {doc.reviewer_notes && (
            <div className="text-xs italic mb-3 text-black/70">“{doc.reviewer_notes}”</div>
          )}
          <div className="flex gap-2 flex-wrap">
            <button
              className="btn-brut text-xs flex-1"
              style={{ boxShadow: "3px 3px 0 0 rgb(34 139 74)" }}
              onClick={() => onVerify(doc.id)}
              data-testid={`admin-vehicle-${testKind}-verify`}
            >
              <CheckCircle2 size={12} /> Approve
            </button>
            <button
              className="btn-brut text-xs flex-1"
              style={{ boxShadow: "3px 3px 0 0 rgb(199 44 44)" }}
              onClick={() => onReject(doc.id)}
              data-testid={`admin-vehicle-${testKind}-reject`}
            >
              <XCircle size={12} /> Reject
            </button>
          </div>
        </>
      ) : (
        <div className="p-6 text-center text-sm text-black/60 border-2 border-dashed border-black/40">
          Not uploaded yet
        </div>
      )}
    </div>
  );
}

function VehicleThumb({ candidateId, docId, fileName }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/admin/candidates/${candidateId}/documents/${docId}`);
        if (!cancelled) setSrc(`data:${r.data.mime_type};base64,${r.data.data_base64}`);
      } catch (err) {
        console.error("Failed to load thumb", err);
      }
    })();
    return () => { cancelled = true; };
  }, [candidateId, docId]);
  if (!src) return <div className="h-48 grid place-items-center font-mono text-[10px] text-black/50">Loading {fileName}…</div>;
  return <img src={src} alt={fileName} className="w-full h-48 object-cover" />;
}

function VehicleSection({ candidate, candidateId, openDoc, verifyDoc }) {
  const docs = candidate.documents || [];
  const truck = docs.find((d) => d.kind === "truck_picture");
  const insurance = docs.find((d) => d.kind === "insurance_certificate");
  const readiness = candidate.readiness_status || "not_ready";

  const readinessLabel = {
    not_ready: "Not ready",
    awaiting_verification: "Awaiting admin verification",
    ready: "Ready",
    vehicle_rejected: "Vehicle rejected",
  }[readiness] || readiness;

  const readinessPillClass = {
    not_ready: "pill-muted",
    awaiting_verification: "pill-flame",
    ready: "pill-success",
    vehicle_rejected: "pill-danger",
  }[readiness] || "pill-muted";

  const rejectWithPrompt = (docId) => {
    const reason = window.prompt("Why is it rejected? (optional)");
    verifyDoc(docId, "rejected", reason || null);
  };

  return (
    <Section
      kicker="Vehicle & Insurance"
      title="Truck + insurance verification"
      right={
        <span
          data-testid="admin-vehicle-readiness"
          className={`pill ${readinessPillClass}`}
        >
          {readinessLabel}
        </span>
      }
    >
      <div className="grid md:grid-cols-2 gap-4">
        <VehiclePanel
          title="Truck picture"
          doc={truck}
          candidateId={candidateId}
          onView={openDoc}
          onVerify={(id) => verifyDoc(id, "verified")}
          onReject={rejectWithPrompt}
          testKind="truck"
        />
        <VehiclePanel
          title="Insurance certificate"
          doc={insurance}
          candidateId={candidateId}
          onView={openDoc}
          onVerify={(id) => verifyDoc(id, "verified")}
          onReject={rejectWithPrompt}
          testKind="insurance"
        />
      </div>
    </Section>
  );
}
